/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { tagName } from '@ember-decorators/component';
import { observes } from '@ember-decorators/object';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { none, not, filterBy } from '@ember/object/computed';
import Component from '@ember/component';
import { isEmpty, isNone } from '@ember/utils';
import { task } from 'ember-concurrency';
import moment from 'moment-timezone';
import readFile from 'cio/utils/read-file';

@tagName('')
export default class Fcm extends Component {
  @service api;
  @service alerts;
  @service analytics;
  @service push;

  // external API
  platform = null;
  onIsDirtyChange() {}
  afterSave() {}

  // internal API
  errors = null;

  @filterBy('errors', 'type', 'config') configErrors;
  @filterBy('errors', 'type', 'sendTest') sendTestErrors;

  isEditing = false;
  selectedFile = null;

  @not('isDisabled') isEnabled;
  @none('push.credentials.fcm') isDisabled;

  @not('isValid') isInvalid;
  @none('validationMessage') isValid;

  @computed('isEnabled', 'push.platforms', 'platform')
  get shouldEnableiOSViaFCM() {
    let isFcmEnabled = this.get('isEnabled');
    let platforms = this.get('push.platforms');
    let currentPlatform = this.get('platform');

    if (currentPlatform !== 'ios') {
      return false;
    }

    let platform = platforms.findBy('key', 'ios');

    if (!isNone(platform)) {
      return platform.provider !== 'fcm' && isFcmEnabled;
    }

    return isFcmEnabled;
  }

  @computed('selectedFile', 'isEditing', 'shouldEnableiOSViaFCM')
  get isDirty() {
    let isEditingIOSviaFCM = this.isEditing && this.shouldEnableiOSViaFCM;
    return !isNone(this.selectedFile) || isEditingIOSviaFCM;
  }

  // we need to be able to propagate isDirty changes up to the controller, as we
  // want to use the ConfirmChanges mixin to prevent route changes when there
  // are unsaved changes
  @observes('isDirty')
  isDirtyChanged() {
    this.get('onIsDirtyChange')(this.get('isDirty'));
  }

  @computed('selectedFile', 'isEnabled', 'shouldEnableiOSViaFCM')
  get validationMessage() {
    if (this.shouldEnableiOSViaFCM) {
      return null;
    }

    if (isEmpty(this.selectedFile)) {
      return 'Upload a .json server key to enable';
    }

    return null;
  }

  init() {
    super.init(...arguments);
    this.set('errors', []);
    this.get('loadCredential').perform();
  }

  loadCredential = task(async () => {
    let credential = await this.get('push.loadCredential').perform('fcm');
    this.analytics.track('push', credential ? 'configuration_viewed' : 'setup_started', {
      platform: this.platform,
    });
  });

  onSave = task(
    {
      restartable: true,
    },
    async () => {
      let password = await this.get('getCredentialsFromFile').perform();

      let isPlatformiOS = this.get('platform') === 'ios';
      let platforms = this.get('push.platforms');
      let ios = platforms.findBy('key', 'ios');
      let canCurrentlySendToiOSViaFCM = !isNone(ios) && ios.provider === 'fcm';
      let allowiOS = isPlatformiOS || canCurrentlySendToiOSViaFCM;

      let data = {
        credential: {
          username: this.get('selectedFile.name'),
          password,
          config: allowiOS ? 'allow_ios' : '',
        },
      };

      try {
        if (this.get('push.providers').includes('apns') && allowiOS) {
          await this.get('push.disableCredential').perform('apns', false);
        }
        await this.get('push.createOrUpdateCredential').perform('fcm', data);
      } catch (e) {
        this._onErrors('config', e.errors);
        return false;
      }

      this.get('alerts').success('push.fcm.enabled', 'Configuration saved!');
      this.setProperties({
        isEditing: false,
        selectedFile: null,
      });
      this.afterSave();

      return true;
    },
  );

  updateConfig = task(async () => {
    if (this.get('push.providers').includes('apns')) {
      await this.get('push.disableCredential').perform('apns', false);
    }

    await this.get('push.updateCredentialConfig').perform('fcm', {
      config: 'allow_ios',
    });

    this.get('alerts').success('push.fcm.updated', 'Configuration saved!');
    this.setProperties({
      isEditing: false,
      selectedFile: null,
    });
    this.afterSave();
  });

  getCredentialsFromFile = task(async () => {
    let file = this.get('selectedFile');
    return await readFile(file);
  });

  _onErrors(type, errors) {
    let currentErrors = [...this.get('errors')];

    // clear send test errors if a config error is reported - config errors
    // supersede send test ones
    if (type === 'config') {
      currentErrors = currentErrors.filterBy('type', 'config');
    }

    (errors || []).forEach((error) => {
      currentErrors.push({
        ...error,
        updated: moment().unix(),
        type,
      });
    });

    this.set('errors', currentErrors);
  }

  @action
  reset() {
    this.setProperties({
      selectedFile: null,
      isEditing: false,
    });
  }

  @action
  onErrors(type, errors) {
    this._onErrors(type, errors);
  }

  @action
  removeError(error) {
    this.set(
      'errors',
      this.errors?.filter((errorFound) => errorFound !== error),
    );
  }
}
